import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import { Alert, Box, Button, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import { useAppSelector } from "~/app/store";
import { AutostoreBin } from "~/features/autostoreBin";
import { AutostoreBinWithAnimation } from "~/features/autostoreBin/AutostoreBinWithAnimation";
import { usePortStatusQuery } from "~/hooks/usePortStatus";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  selectIsShortSideBin,
  selectThisWorkstation
} from "~/redux/selectors/workstationsSelectors";
import { useGetBinConfigurationsQuery } from "~/redux/warehouse/bin.hooks";

import { ConfirmBinReconfigurationButton } from "./ConfirmBinReconfigurationButton";

export const ReconfigurationPort = ({
  portId,
  portSideIndex
}: {
  portId: number;
  portSideIndex: number;
}) => {
  const isShortSideBin = useAppSelector(selectIsShortSideBin);
  const [queryParams] = useSearchParams();
  const targetBinConfigurationId = queryParams.get("targetBinConfigurationId");
  const workstation = useAppSelector(selectThisWorkstation);
  const trackedPageName = useMixpanelPageName();

  const {
    binPortSideIndex,
    binState,
    error,
    activityState,
    refetch,
    ...portStatus
  } = usePortStatusQuery(portId, portSideIndex);
  const { selectedBin } = portStatus;

  const shouldHideBinId = activityState === "Inactive";

  const { data: binConfigurations } = useGetBinConfigurationsQuery();

  const targetBinConfiguration = binConfigurations?.find(
    (bc) => bc.configurationId === targetBinConfigurationId
  );

  const handleRetryPortStatus = () => {
    mixpanelTrack({
      trackedPageName,
      label: "Retry Port Status",
      type: "Button Click",
      eventProperties: {
        "Port Id": portId,
        "Port Side Index": portSideIndex
      }
    });
    void refetch();
  };

  return (
    <>
      <Box
        alignContent="center"
        display="flex"
        height="100%"
        flexDirection="column"
        justifyContent="center"
      >
        {!!error && portSideIndex === 0 && (
          <Alert
            sx={{
              gridColumn: workstation?.multiPortEnabled ? "span 2" : "unset"
            }}
            variant="outlined"
            severity="error"
            action={<Button onClick={handleRetryPortStatus}>Retry</Button>}
          >
            {getMessageFromRtkError(error)}
          </Alert>
        )}
        {!error && (
          <AutostoreBinWithAnimation
            portId={portId}
            portSideIndex={portSideIndex}
            binId={selectedBin}
            pickQuantity={""}
            pickCompartment={null}
            numberOfRows={
              (portSideIndex === binPortSideIndex &&
                binState?.binConfiguration?.horizontalCompartmentCount) ||
              1
            }
            numberOfColumns={
              (portSideIndex === binPortSideIndex &&
                binState?.binConfiguration?.verticalCompartmentCount) ||
              1
            }
            hideBinId={true}
            isShortSideBin={isShortSideBin}
          />
        )}
        {!error && !shouldHideBinId && !!selectedBin && (
          <Typography variant="h5" textAlign="center" mt={2}>
            Bin {selectedBin}
          </Typography>
        )}
      </Box>
      <KeyboardDoubleArrowDownIcon
        sx={{
          fontSize: "3rem",
          placeSelf: "center"
        }}
      />
      <Box
        alignContent="center"
        display="flex"
        height="100%"
        flexDirection="column"
        justifyContent="center"
      >
        <AutostoreBin
          state={"Port Closed"}
          numberOfColumns={
            targetBinConfiguration?.verticalCompartmentCount || 1
          }
          numberOfRows={targetBinConfiguration?.horizontalCompartmentCount || 1}
          pickQuantity=""
          pickCompartment={null}
          isShortSideBin={isShortSideBin}
        />
      </Box>
      <ConfirmBinReconfigurationButton
        portId={portId}
        portSideIndex={portSideIndex}
      />
    </>
  );
};
